import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { QuizQuestion, QuizSubmission, QuizQuestionAPI } from '../types';
import { quizService } from '../services/quizService';

interface QuizProps {
    courseId: string;
    onComplete: (result: any) => void;
    onBack?: () => void;
}

export function Quiz({ courseId, onComplete, onBack }: QuizProps) {
    const [questions, setQuestions] = useState<QuizQuestion[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Add safety check for courseId
    if (!courseId) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="text-center">
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Invalid Course</h3>
                    <p className="text-slate-600">Course ID is missing. Please go back and try again.</p>
                </div>
            </div>
        );
    }

    useEffect(() => {
        loadQuestions();
    }, [courseId]);

    const loadQuestions = async () => {
        try {
            setLoading(true);
            setError(null);
            const apiResponse = await quizService.getQuizQuestions(courseId);
            
            // Ensure we have an array
            if (!Array.isArray(apiResponse)) {
                console.error('Questions data is not an array:', apiResponse);
                throw new Error('Questions data is not an array');
            }
            
            // Transform API response to match expected format
            const transformedQuestions: QuizQuestion[] = apiResponse.map((question: QuizQuestionAPI) => ({
                id: question.id,
                question: question.question,
                options: question.options.map((optionText: string, index: number) => ({
                    id: `${question.id}-option-${index}`,
                    text: optionText,
                    isCorrect: index === question.correct_answer
                }))
            }));
            
            setQuestions(transformedQuestions);
        } catch (err) {
            console.error('Error loading quiz questions:', err);
            setError('Failed to load quiz questions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAnswer = (questionId: string, optionId: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: optionId
        }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            setError(null);

            // Convert answers to submission format
            // Extract option index from the selectedOptionId (format: questionId-option-index)
            const submissions: QuizSubmission[] = Object.entries(answers).map(([questionId, selectedOptionId]) => {
                // Extract the option index from the selectedOptionId and add 1 for 1-based indexing
                const optionIndex = parseInt(selectedOptionId.split('-option-')[1]) + 1;
                return {
                    questionId,
                    selected_answer: optionIndex
                };
            });

            const result = await quizService.submitQuiz(courseId, submissions);
            onComplete(result);
        } catch (err) {
            console.error('Error submitting quiz:', err);
            setError('Failed to submit quiz. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-sky-600 mr-3" />
                    <span className="text-lg text-slate-600">Loading quiz questions...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="text-center">
                    <div className="text-red-500 mb-4">
                        <CheckCircle size={48} className="mx-auto" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Error Loading Quiz</h3>
                    <p className="text-slate-600 mb-4">{error}</p>
                    <button
                        onClick={loadQuestions}
                        className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="text-center">
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Quiz Available</h3>
                    <p className="text-slate-600">This course doesn't have a quiz yet.</p>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];

    // Add safety check for currentQuestion
    if (!currentQuestion) {
        return (
            <div className="bg-white p-8 rounded-lg shadow border border-slate-200">
                <div className="text-center">
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Quiz Error</h3>
                    <p className="text-slate-600">Unable to load the current question. Please try again.</p>
                    <button
                        onClick={loadQuestions}
                        className="mt-4 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                    >
                        Reload Quiz
                    </button>
                </div>
            </div>
        );
    }

    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const isFirstQuestion = currentQuestionIndex === 0;
    const hasAnswered = answers[currentQuestion.id] !== undefined;
    const allQuestionsAnswered = questions.every(q => answers[q.id] !== undefined);

    return (
        <div className="bg-white p-6 rounded-lg shadow border border-slate-200">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold text-slate-800">Course Quiz</h2>
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="flex items-center text-slate-600 hover:text-slate-800"
                        >
                            <ArrowLeft size={20} className="mr-1" />
                            Back to Course
                        </button>
                    )}
                </div>
                
                {/* Progress */}
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-500">
                        Question {currentQuestionIndex + 1} of {questions.length}
                    </span>
                    <span className="text-sm text-slate-500">
                        {Object.keys(answers).length} of {questions.length} answered
                    </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                        className="bg-sky-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    />
                </div>
            </div>

            {/* Question */}
            <div className="mb-6">
                <h3 className="text-xl font-medium text-slate-900 mb-6">
                    {currentQuestion.question}
                </h3>

                <div className="space-y-3">
                    {currentQuestion.options.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => handleAnswer(currentQuestion.id, option.id)}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                                answers[currentQuestion.id] === option.id
                                    ? 'border-sky-500 bg-sky-50 text-sky-900'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex items-center">
                                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                                    answers[currentQuestion.id] === option.id
                                        ? 'border-sky-500 bg-sky-500'
                                        : 'border-slate-300'
                                }`}>
                                    {answers[currentQuestion.id] === option.id && (
                                        <div className="w-2 h-2 bg-white rounded-full m-0.5" />
                                    )}
                                </div>
                                <span className="text-slate-800">{option.text}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-slate-200">
                <button
                    onClick={handlePrevious}
                    disabled={isFirstQuestion}
                    className="flex items-center px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ArrowLeft size={16} className="mr-1" />
                    Previous
                </button>

                <div className="flex items-center space-x-2">
                    {!isLastQuestion ? (
                        <button
                            onClick={handleNext}
                            disabled={!hasAnswered}
                            className="flex items-center px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <ArrowRight size={16} className="ml-1" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!allQuestionsAnswered || submitting}
                            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={16} className="mr-2" />
                                    Submit Quiz
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            )}
        </div>
    );
}
